import { useEffect, useRef, useState } from "react";
import ContainerLoading from "../common/loading/ContainerLoading";
import { motion } from "framer-motion";
import { useToast } from "../contexts/ToastContext";
import { useNavigate, useLocation } from "react-router-dom";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";
import Icon from "../common/Icon";
import { useCart } from "../contexts/CartContext";
import ExpressCheckoutPersonalDetailsForm from "./ExpressCheckoutPersonalDetailsForm";
import ExpressCheckoutAddressForm from "./ExpressCheckoutAddressForm";
import CartSummary from "./CartSummary";

const ExpressCheckoutForm = () => {
  const { cart, updateCartQuantity, removeFromCart, itemLoading } = useCart();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation(); // ✅ detect if coming from payment

  const [showAddressForm, setShowAddressForm] = useState(false);

  const cartSummaryRef = useRef(null);

  // ✅ If user came from payment page, directly show address form
  useEffect(() => {
    if (location.state?.from === "payment") {
      setShowAddressForm(true);
    }
  }, [location.state]);

  // ✅ Scroll & highlight delivery fee
  const scrollToDeliveryFee = () => {
    if (!cartSummaryRef.current) return;
    const deliveryEl = cartSummaryRef.current.querySelector("#delivery-fee");
    if (deliveryEl) {
      deliveryEl.scrollIntoView({ behavior: "smooth", block: "center" });
      deliveryEl.classList.add("highlight");
      setTimeout(() => deliveryEl.classList.remove("highlight"), 2000);
    }
  };

  return (
    <>
      <motion.div
        initial={{ x: -200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -200, opacity: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <p className="text-34pt pb-4 lg:pb-8 2.5xl:pb-9">Express Checkout</p>
        <div className="grid grid-cols-1 lg:grid-cols-1 2xl:grid-cols-[25.5fr_1fr] 2.5xl:grid-cols-[11.5fr_1fr] gap-8 items-start">
          <div>
            <div className="bg-[#DEE2DD]/40 w-full rounded-[15px] p-5 md:px-10 md:py-10 2.5xl:px-24 2.5xl:py-[70px] mb-4 md:mb-6 lg:mb-8">
              <p className="text-green text-21pt">
                Review all items before proceeding to checkout
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] 2.5xl:grid-cols-[1.08fr_0.805fr] gap-5 md:gap-10 2.5xl:gap-24 mt-6 items-start">
                {/* Left Column - Cart Items */}
                <div className="grid grid-cols-1 gap-4 md:gap-5">
                  {cart?.orderCart?.items?.length > 0 ? (
                    cart?.orderCart?.items.map((item, index) => (
                     <div
                      key={index}
                      className="bg-white rounded-lg p-4 flex flex-col md:flex-row items-start lg:items-center justify-between shadow-sm relative min-h-[130px]"
                    >
                      {/* Loading overlay */}
                      {itemLoading === index && (
                        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20 rounded-lg">
                          <ContainerLoading />
                        </div>
                      )}

                      {/* Product Image */}
                      <img
                        src={
                          process.env.REACT_APP_BACKEND_URL +
                          "public/" +
                          item?.product?.image
                        }
                        alt={item.product?.title}
                        className="w-20 h-20 md:w-28 md:h-28 object-cover rounded-lg bg-gray mb-2 md:mb-0"
                        onError={(e) => {
                          e.currentTarget.src = require("../../assets/images/link.png");
                        }}
                      />

                      {/* Product Details */}
                      <div className="flex-1 flex flex-col justify-between md:ms-4 md:me-10">
                        <div>
                          <p className="text-base">{item.product?.title}</p>
                          <p className="text-orange mt-1">
                            {item?.product?.salePrice} AED
                          </p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-3 mt-4">
                          <button
                            title={
                              item.quantity > 1
                                ? "Click to decrease product quantity"
                                : "Minimum quantity reached"
                            }
                            onClick={() =>
                              item.quantity > 1 &&
                              updateCartQuantity(index, "sub")
                            }
                            disabled={item.quantity <= 1}
                            className={`rounded p-1 transition ${
                              item.quantity > 1
                                ? "bg-lightGray hover:bg-gray"
                                : "bg-gray-200 cursor-not-allowed opacity-60"
                            }`}
                          >
                            <AiOutlineMinus />
                          </button>

                          <span>{item.quantity}</span>

                          <button
                            title="Click to increase product quantity"
                            onClick={() => updateCartQuantity(index, "add")}
                            className="bg-lightGray rounded p-1 hover:bg-gray"
                          >
                            <AiOutlinePlus />
                          </button>
                        </div>
                      </div>

                      {/* Delete Button */}
                      <button
                        title="Click to remove item from cart"
                        onClick={() => removeFromCart(index)}
                        className="absolute top-5 right-5 text-green hover:text-secondary"
                      >
                        <Icon icon="delete-product" size={25} />
                      </button>
                    </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No items in the cart</p>
                  )}
                </div>

                {/* Right Column - Summary */}
                <CartSummary cart={cart?.orderCart} ref={cartSummaryRef} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-1 2xl:grid-cols-[1.4fr_1fr] 2.5xl:grid-cols-[1.2fr_1fr] gap-8 items-start mb-6 xl:mb-0">
        <div>
         
            <ExpressCheckoutPersonalDetailsForm
              onSuccess={() => navigate("/express-checkout-address")}
            />
        
            {/* <ExpressCheckoutAddressForm
              onCitySelect={scrollToDeliveryFee}
              onBack={() => setShowAddressForm(false)}
            /> */}
        
        </div>
      </div>
    </>
  );
};

export default ExpressCheckoutForm;
